/**
 * The dashboard's pure navigation model (spec §1.4, §1.6, §2.1): the open flag, the tab (0..7, number = index + 1),
 * the remembered section per tab and the depth (0 = tab and section strip, 1 = list, 2 = detail; at most three
 * levels). Inputs come from `keyToDashInput` (Q/E tab, 1–8 jump, Z/C section, Enter in, Backspace back) and from
 * the shell (Esc, close). Mutated in place like the pause menu's model (`hud/menu.ts`); no DOM.
 */
import { DASH_TABS, type DashTabId } from './registry'

export const DASH_TAB_COUNT = DASH_TABS.length
/** Deepest level: tab → list → detail. */
export const MAX_DEPTH = 2

export interface DashModel {
  open: boolean
  /** Index into `DASH_TABS`. */
  tab: number
  /** The last section index per tab (reopening returns there). */
  readonly sections: number[]
  depth: number
  /** False until the first open of the session (which goes to Overview). */
  opened: boolean
  /** The focus key handed to the section (deep links); cleared on any navigation. */
  focus: string | undefined
}

export type DashCommand = 'prevTab' | 'nextTab' | 'prevSection' | 'nextSection' | 'enter' | 'back' | 'escape' | 'close'
/** A command, or a tab index 0..7 (the 1–8 jump keys). */
export type DashInput = DashCommand | number

/** What the shell must do after a step: nothing, re-render, close (and re-lock), or open the pause menu over it. */
export type DashOutcome = 'none' | 'changed' | 'close' | 'menu'

export function createDashModel(): DashModel {
  return { open: false, tab: 0, sections: DASH_TABS.map(() => 0), depth: 0, opened: false, focus: undefined }
}

export function dashTabId(m: DashModel): DashTabId {
  return DASH_TABS[m.tab]
}

/** Keys inside the dashboard (spec §1.6); null when the key is not the dashboard's. Repeats are ignored. */
export function keyToDashInput(code: string, repeat: boolean): DashInput | null {
  if (repeat) return null
  switch (code) {
    case 'KeyQ':
      return 'prevTab'
    case 'KeyE':
      return 'nextTab'
    case 'KeyZ':
      return 'prevSection'
    case 'KeyC':
      return 'nextSection'
    case 'Enter':
    case 'NumpadEnter':
    case 'Space':
      return 'enter'
    case 'Backspace':
      return 'back'
  }
  if (code.length === 6 && code.startsWith('Digit')) {
    const n = code.charCodeAt(5) - 49
    if (n >= 0 && n < DASH_TAB_COUNT) return n
  }
  return null
}

function setTab(m: DashModel, tab: number): void {
  m.tab = tab
  m.depth = 0
  m.focus = undefined
}

/**
 * Applies one input. `sectionCount(tab)` is how many sections the tab has (Overview: 0). Enter goes one level in
 * only where there are sections; Back and Esc go one level out; at the top Back closes and Esc opens the menu.
 */
export function stepDash(m: DashModel, input: DashInput, sectionCount: (tab: number) => number): DashOutcome {
  if (!m.open) return 'none'
  if (typeof input === 'number') {
    if (input < 0 || input >= DASH_TAB_COUNT) return 'none'
    if (input === m.tab && m.depth === 0) return 'none'
    setTab(m, input)
    return 'changed'
  }
  switch (input) {
    case 'prevTab':
      setTab(m, (m.tab + DASH_TAB_COUNT - 1) % DASH_TAB_COUNT)
      return 'changed'
    case 'nextTab':
      setTab(m, (m.tab + 1) % DASH_TAB_COUNT)
      return 'changed'
    case 'prevSection':
    case 'nextSection': {
      const n = sectionCount(m.tab)
      if (n <= 1) return 'none'
      const cur = Math.min(m.sections[m.tab], n - 1)
      m.sections[m.tab] = (cur + (input === 'nextSection' ? 1 : n - 1)) % n
      m.depth = 0
      m.focus = undefined
      return 'changed'
    }
    case 'enter':
      if (sectionCount(m.tab) === 0 || m.depth >= MAX_DEPTH) return 'none'
      m.depth++
      return 'changed'
    case 'back':
      if (m.depth === 0) return 'close'
      m.depth--
      m.focus = undefined
      return 'changed'
    case 'escape':
      if (m.depth === 0) return 'menu'
      m.depth--
      m.focus = undefined
      return 'changed'
    case 'close':
      return 'close'
  }
}

/** Where a deep link points, resolved by the shell from the registry. */
export interface DashLanding {
  readonly tab: number
  /** Section index within the tab, or -1 for the remembered one. */
  readonly section: number
  readonly focus?: string
}

/**
 * Opens the model. With a landing it goes there (a focus key opens the list level); without one the first open of
 * the session goes to Overview and later opens return to the last tab and section.
 */
export function openDash(m: DashModel, at?: DashLanding): void {
  m.open = true
  if (at !== undefined) {
    m.tab = at.tab
    if (at.section >= 0) m.sections[at.tab] = at.section
    m.focus = at.focus
    m.depth = at.focus !== undefined ? 1 : 0
  } else if (!m.opened) {
    m.tab = 0
    m.depth = 0
    m.focus = undefined
  } else {
    m.depth = 0
  }
  m.opened = true
}

export function closeDash(m: DashModel): void {
  m.open = false
  m.depth = 0
  m.focus = undefined
}
